import WebBluetoothClient from './web_bluetooth_client';
import WebBluetoothEvents from './web_bluetooth_events';

export default class WebBluetoothRpcClient extends WebBluetoothClient {
	// G2+ service and characteristic UUIDs
	public static readonly DEVICE_SERVICE_UUID: BluetoothServiceUUID =
		'5f6d4f53-5f52-5043-5f53-56435f49445f';

	private readonly W_UUID: BluetoothCharacteristicUUID = '5f6d4f53-5f52-5043-5f74-785f63746c5f'; //W
	private readonly RW_UUID: BluetoothCharacteristicUUID = '5f6d4f53-5f52-5043-5f64-6174615f5f5f'; //RW
	private readonly READ_NOTIFY_UUID: BluetoothCharacteristicUUID =
		'5f6d4f53-5f52-5043-5f72-785f63746c5f'; //RN

	private charW: BluetoothRemoteGATTCharacteristic;
	private charRW: BluetoothRemoteGATTCharacteristic;
	private charReadNotify: BluetoothRemoteGATTCharacteristic;

	private doesNotSupportNotifications = false;

	constructor(connectedDevice: BluetoothDevice) {
		super(connectedDevice);
		this.connect();
	}

	/**
	 * Send an RPC request to the G2+ BLE device.
	 * @param {string} method - The RPC method to call.
	 * @param {Object} [params=null] - The parameters for the RPC method.
	 * @param {string} [auth=null] - The authentication token.
	 */
	public async call(method: string, params: any = null, auth: string = null) {
		if (import.meta.env.DEV) {
			console.log('RPC BLE Request', { method }, { params }, { auth });
		}
		try {
			if (!this.isConnected) await this.connect();
			const requestData: {
				id: string;
				method: string;
				src: 'shelly-app';
				params?: {};
				auth: string;
			} = {
				id: `${method}:${Date.now().toString()}`,
				method,
				src: 'shelly-app',
				auth,
			};
			if (params) requestData.params = params;
			const lengthBuffer: ArrayBuffer = new ArrayBuffer(4);
			const jsonRequestData: string = JSON.stringify(requestData);
			const view: DataView = new DataView(lengthBuffer);
			view.setUint32(0, jsonRequestData.length, false);
			await this.charW.writeValue(lengthBuffer);
			await this._sleep(1000);
			await this.charRW.writeValue(new TextEncoder().encode(jsonRequestData));
			if (this.doesNotSupportNotifications) {
				const value = await this.charReadNotify.readValue();
				this._processNotification(value);
			}
		} catch (error) {
			this._onError({
				type: WebBluetoothEvents.Errors.DATA_ERROR,
				message: error?.message || error,
			});
		}
	}
	public async disconnect() {
		if (this.charReadNotify && this.supportNotifications) {
			if (this.isConnected) await this.charReadNotify.stopNotifications();
			this.charReadNotify.removeEventListener(
				'characteristicvaluechanged',
				this._handleNotifications,
			);
		}
		super.disconnect();
	}

	public get supportNotifications(): boolean {
		return !this.doesNotSupportNotifications;
	}

	protected async initService() {
		try {
			const service: BluetoothRemoteGATTService = await this.server.getPrimaryService(
				WebBluetoothRpcClient.DEVICE_SERVICE_UUID,
			);
			this.charRW = await service.getCharacteristic(this.RW_UUID);
			this.charW = await service.getCharacteristic(this.W_UUID);
			this.charReadNotify = await service.getCharacteristic(this.READ_NOTIFY_UUID);
			await this.charReadNotify
				.startNotifications()
				.then(() => {
					this.doesNotSupportNotifications = false;
					this.charReadNotify.addEventListener(
						'characteristicvaluechanged',
						this._handleNotifications,
					);
				})
				.catch(() => (this.doesNotSupportNotifications = true));
			this._dispatchEvent(WebBluetoothEvents.CONNECTED);
		} catch (error) {
			this._onError({
				type: WebBluetoothEvents.Errors.SERVICE_ERROR,
				message: error?.message || error,
			});
		}
	}
	private _handleNotifications = (e: Event) => {
		this._processNotification(e.target['value']);
	};
	private async _processNotification(value: DataView) {
		try {
			const resultInt = value.getUint32(0, false);
			let count = 0;
			let response = '';
			while (count < resultInt) {
				await this._sleep(1000);
				const resultChunk = await this.charRW.readValue();
				count += resultChunk.byteLength;
				response += new TextDecoder().decode(resultChunk);
			}
			const result = JSON.parse(response) || {};
			result.method = result.id.split(':')[0];
			this._dispatchEvent(WebBluetoothEvents.DATA, result);
		} catch (error) {
			this._onError({
				type: WebBluetoothEvents.Errors.DATA_ERROR,
				message: error?.message || error,
			});
		}
	}
}
